import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { 
  Loader2, Wand2, Users, MapPin, AlertTriangle, 
  CheckCircle, Search, X, Calendar, Clock
} from "lucide-react";
import {
  InjurySeverity,
  AreaSafetyStatus,
  INJURY_SEVERITY_LABELS,
  AREA_SAFETY_LABELS,
} from "@/types/incident";

interface Worker {
  id: string;
  display_name: string;
  role: string;
  team: string | null;
}

interface ExtractedContext {
  people_involved: { name: string; worker_id: string | null; role_in_incident: string; matched_confidence: number }[];
  mechanism_of_injury: string | null;
  hazard_category: string | null;
  body_part: string | null;
  work_activity: string | null;
  location: string | null;
  inferred_severity: string | null;
  confidence: number;
  linked_signals: { type: string; id: string; title: string; relevance: string }[];
}

interface ManualIncidentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyId: string;
  siteId?: string | null;
  onSuccess: () => void;
}

export function ManualIncidentDialog({ 
  open, 
  onOpenChange, 
  companyId, 
  siteId,
  onSuccess 
}: ManualIncidentDialogProps) {
  const { toast } = useToast();
  
  // Form state
  const [description, setDescription] = useState("");
  const [location, setLocation] = useState("");
  const [incidentDatetime, setIncidentDatetime] = useState(
    new Date().toISOString().slice(0, 16)
  );
  const [shift, setShift] = useState("");
  const [injurySeverity, setInjurySeverity] = useState<InjurySeverity | "">("");
  const [areaSafetyStatus, setAreaSafetyStatus] = useState<AreaSafetyStatus | "">("");
  const [bodyPart, setBodyPart] = useState("");
  const [mechanismOfInjury, setMechanismOfInjury] = useState("");
  const [immediateTreatment, setImmediateTreatment] = useState("");
  const [reportedByName, setReportedByName] = useState("");
  
  // Workers/people involved
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [selectedWorkers, setSelectedWorkers] = useState<string[]>([]);
  const [workerSearch, setWorkerSearch] = useState("");
  const [showWorkerDropdown, setShowWorkerDropdown] = useState(false);
  
  // AI extraction
  const [extractedContext, setExtractedContext] = useState<ExtractedContext | null>(null);
  const [isExtracting, setIsExtracting] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      loadWorkers();
      resetForm();
    }
  }, [open, companyId, siteId]);

  const loadWorkers = async () => {
    let query = supabase
      .from("worker_profiles")
      .select("id, display_name, role, team")
      .eq("company_id", companyId);
    
    if (siteId) {
      query = query.eq("site_id", siteId);
    }
    
    const { data } = await query.order("display_name");
    setWorkers(data || []);
  };

  const resetForm = () => {
    setDescription("");
    setLocation("");
    setIncidentDatetime(new Date().toISOString().slice(0, 16));
    setShift("");
    setInjurySeverity("");
    setAreaSafetyStatus("");
    setBodyPart("");
    setMechanismOfInjury("");
    setImmediateTreatment("");
    setReportedByName("");
    setSelectedWorkers([]);
    setExtractedContext(null);
  };

  const handleAIExtract = async () => {
    if (!description.trim()) {
      toast({
        title: "Deskripsi kosong",
        description: "Masukkan deskripsi atau transkrip insiden",
        variant: "destructive",
      });
      return;
    }

    setIsExtracting(true);
    try {
      const { data, error } = await supabase.functions.invoke("extract-incident-context", {
        body: {
          description,
          company_id: companyId,
          site_id: siteId,
        },
      });

      if (error) throw error;

      setExtractedContext(data);

      // Auto-fill form with extracted data
      if (data.location && !location) setLocation(data.location);
      if (data.mechanism_of_injury && !mechanismOfInjury) setMechanismOfInjury(data.mechanism_of_injury);
      if (data.body_part && !bodyPart) setBodyPart(data.body_part);
      if (data.inferred_severity && !injurySeverity) {
        const severityMap: Record<string, InjurySeverity> = {
          no_injury: "no_injury",
          first_aid: "first_aid",
          medical_treatment: "medical_treatment",
          lost_time: "lost_time",
          fatality: "fatality",
        };
        if (severityMap[data.inferred_severity]) {
          setInjurySeverity(severityMap[data.inferred_severity]);
        }
      }

      // Auto-select matched workers
      const matchedWorkerIds = (data.people_involved || [])
        .filter((p: any) => p.worker_id)
        .map((p: any) => p.worker_id);
      
      if (matchedWorkerIds.length > 0) {
        setSelectedWorkers(prev => [...new Set([...prev, ...matchedWorkerIds])]);
      }

      toast({
        title: "Ekstraksi berhasil",
        description: `Ditemukan ${data.people_involved?.length || 0} orang, ${data.linked_signals?.length || 0} sinyal terkait`,
      });
    } catch (error) {
      console.error("Extraction error:", error);
      toast({
        title: "Gagal ekstraksi",
        description: "Tidak dapat mengekstrak informasi dari deskripsi",
        variant: "destructive",
      });
    } finally {
      setIsExtracting(false);
    }
  };

  const handleSubmit = async () => {
    if (!description.trim() || !location.trim() || !injurySeverity || !areaSafetyStatus) {
      toast({
        title: "Data tidak lengkap",
        description: "Mohon lengkapi deskripsi, lokasi, keparahan, dan status area",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const { data, error } = await supabase.functions.invoke("create-incident", {
        body: {
          reported_by: null, // Admin-entered
          company_id: companyId,
          site_id: siteId,
          location,
          description,
          incident_datetime: incidentDatetime,
          injury_severity: injurySeverity,
          body_part: bodyPart || undefined,
          mechanism_of_injury: mechanismOfInjury || undefined,
          immediate_treatment: immediateTreatment || undefined,
          area_safety_status: areaSafetyStatus,
          shift: shift || undefined,
          people_involved: selectedWorkers.length > 0 ? selectedWorkers : undefined,
          hazard_category: extractedContext?.hazard_category,
          report_source: "admin_entry",
          reported_by_name: reportedByName || undefined,
        },
      });

      if (error) throw error;

      // Store AI extracted context
      if (extractedContext && data?.incident_id) {
        await supabase
          .from("workplace_incidents")
          .update({ ai_extracted_context: JSON.parse(JSON.stringify(extractedContext)) })
          .eq("id", data.incident_id);
      }

      toast({
        title: "Insiden berhasil ditambahkan",
        description: areaSafetyStatus === "still_unsafe"
          ? "HSE dan Supervisor telah diberitahu"
          : undefined,
      });

      onOpenChange(false);
      onSuccess();
    } catch (error) {
      console.error("Submit error:", error);
      toast({
        title: "Gagal menyimpan insiden",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleWorker = (workerId: string) => {
    setSelectedWorkers(prev => 
      prev.includes(workerId)
        ? prev.filter(id => id !== workerId)
        : [...prev, workerId]
    );
  };

  const filteredWorkers = workers.filter(w => 
    w.display_name.toLowerCase().includes(workerSearch.toLowerCase()) ||
    w.role?.toLowerCase().includes(workerSearch.toLowerCase())
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            Tambah Insiden Manual
          </DialogTitle>
          <DialogDescription>
            Masukkan detail insiden yang dilaporkan melalui radio, WhatsApp, telepon, atau langsung
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[calc(90vh-180px)] pr-4">
          <div className="space-y-6 py-4">
            {/* Description with AI Extract */}
            <div className="space-y-2">
              <Label htmlFor="description">Deskripsi / Transkrip Laporan *</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Masukkan deskripsi kejadian atau transkrip dari radio/WhatsApp/telepon..."
                className="min-h-[120px]"
              />
              <Button
                type="button"
                variant="outline"
                onClick={handleAIExtract}
                disabled={isExtracting || !description.trim()}
                className="w-full"
              >
                {isExtracting ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Wand2 className="h-4 w-4 mr-2" />
                )}
                Ekstrak dengan AI
              </Button>
            </div>

            {/* Extracted Context Preview */}
            {extractedContext && (
              <div className="p-4 bg-primary/5 rounded-lg space-y-3">
                <div className="flex items-center gap-2 font-medium text-sm">
                  <CheckCircle className="h-4 w-4 text-success" />
                  Hasil Ekstraksi AI (confidence: {Math.round(extractedContext.confidence * 100)}%)
                </div>
                
                {extractedContext.people_involved?.length > 0 && (
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">Orang Terdeteksi:</p>
                    <div className="flex flex-wrap gap-1">
                      {extractedContext.people_involved.map((p, i) => (
                        <Badge 
                          key={i} 
                          variant={p.worker_id ? "default" : "secondary"}
                          className="text-xs"
                        >
                          {p.name} ({p.role_in_incident})
                          {p.worker_id && " ✓"}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {extractedContext.linked_signals?.length > 0 && (
                  <div className="p-2 bg-warning/10 rounded text-xs">
                    <p className="font-medium text-warning">
                      ⚠️ {extractedContext.linked_signals.length} Sinyal Terkait Terdeteksi
                    </p>
                    <ul className="mt-1 space-y-1">
                      {extractedContext.linked_signals.slice(0, 3).map((signal, i) => (
                        <li key={i} className="text-muted-foreground">
                          • {signal.title}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Reporter Name */}
            <div className="space-y-2">
              <Label htmlFor="reportedByName">Nama Pelapor (jika bukan pekerja terdaftar)</Label>
              <Input
                id="reportedByName"
                value={reportedByName}
                onChange={(e) => setReportedByName(e.target.value)}
                placeholder="Contoh: Pak Budi via HT"
              />
            </div>

            {/* Location & DateTime */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="location">Lokasi *</Label>
                <Input
                  id="location"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="Area kejadian"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="datetime">Waktu Kejadian *</Label>
                <Input
                  id="datetime"
                  type="datetime-local"
                  value={incidentDatetime}
                  onChange={(e) => setIncidentDatetime(e.target.value)}
                />
              </div>
            </div>

            {/* Shift */}
            <div className="space-y-2">
              <Label>Shift</Label>
              <Select value={shift} onValueChange={setShift}>
                <SelectTrigger>
                  <SelectValue placeholder="Pilih shift" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pagi">Shift Pagi</SelectItem>
                  <SelectItem value="siang">Shift Siang</SelectItem>
                  <SelectItem value="malam">Shift Malam</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Severity */}
            <div className="space-y-2">
              <Label>Tingkat Keparahan *</Label>
              <div className="grid grid-cols-5 gap-2">
                {(Object.keys(INJURY_SEVERITY_LABELS) as InjurySeverity[]).map((sev) => {
                  const info = INJURY_SEVERITY_LABELS[sev];
                  const isSelected = injurySeverity === sev;
                  return (
                    <button
                      key={sev}
                      type="button"
                      onClick={() => setInjurySeverity(sev)}
                      className={cn(
                        "p-2 rounded-lg border-2 flex flex-col items-center gap-1 transition-all text-xs",
                        isSelected
                          ? "border-primary bg-primary/10"
                          : "border-border hover:border-primary/50"
                      )}
                    >
                      <span className="text-lg">{info.icon}</span>
                      <span className="font-medium line-clamp-1">{info.labelId}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Injury Details */}
            {injurySeverity && injurySeverity !== "no_injury" && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Bagian Tubuh</Label>
                  <Input
                    value={bodyPart}
                    onChange={(e) => setBodyPart(e.target.value)}
                    placeholder="Contoh: tangan kanan"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Mekanisme Cedera</Label>
                  <Input
                    value={mechanismOfInjury}
                    onChange={(e) => setMechanismOfInjury(e.target.value)}
                    placeholder="Contoh: terjatuh, tertimpa"
                  />
                </div>
                <div className="col-span-2 space-y-2">
                  <Label>Pertolongan Pertama</Label>
                  <Input
                    value={immediateTreatment}
                    onChange={(e) => setImmediateTreatment(e.target.value)}
                    placeholder="Contoh: dibersihkan dan diperban"
                  />
                </div>
              </div>
            )}

            {/* Area Safety Status */}
            <div className="space-y-2">
              <Label>Status Area Saat Ini *</Label>
              <div className="grid grid-cols-3 gap-2">
                {(Object.keys(AREA_SAFETY_LABELS) as AreaSafetyStatus[]).map((status) => {
                  const info = AREA_SAFETY_LABELS[status];
                  const isSelected = areaSafetyStatus === status;
                  return (
                    <button
                      key={status}
                      type="button"
                      onClick={() => setAreaSafetyStatus(status)}
                      className={cn(
                        "p-3 rounded-lg border-2 flex items-center justify-center gap-2 transition-all",
                        isSelected
                          ? `border-current ${info.color}`
                          : "border-border hover:border-primary/50"
                      )}
                    >
                      <span>{info.icon}</span>
                      <span className="text-sm font-medium">{info.labelId}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* People Involved Picker */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                Orang Terlibat
              </Label>
              
              {/* Selected Workers */}
              {selectedWorkers.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2">
                  {selectedWorkers.map(workerId => {
                    const worker = workers.find(w => w.id === workerId);
                    return (
                      <Badge key={workerId} variant="secondary" className="gap-1">
                        {worker?.display_name || workerId}
                        <button
                          type="button"
                          onClick={() => toggleWorker(workerId)}
                          className="ml-1 hover:text-destructive"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    );
                  })}
                </div>
              )}

              {/* Worker Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  value={workerSearch}
                  onChange={(e) => setWorkerSearch(e.target.value)}
                  onFocus={() => setShowWorkerDropdown(true)}
                  placeholder="Cari pekerja..."
                  className="pl-9"
                />
                
                {showWorkerDropdown && workerSearch && (
                  <div className="absolute z-10 w-full mt-1 bg-popover border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {filteredWorkers.length === 0 ? (
                      <p className="p-3 text-sm text-muted-foreground">Tidak ditemukan</p>
                    ) : (
                      filteredWorkers.slice(0, 10).map(worker => (
                        <button
                          key={worker.id}
                          type="button"
                          onClick={() => {
                            toggleWorker(worker.id);
                            setWorkerSearch("");
                            setShowWorkerDropdown(false);
                          }}
                          className={cn(
                            "w-full px-3 py-2 text-left hover:bg-muted flex items-center justify-between",
                            selectedWorkers.includes(worker.id) && "bg-primary/10"
                          )}
                        >
                          <div>
                            <p className="font-medium text-sm">{worker.display_name}</p>
                            <p className="text-xs text-muted-foreground">
                              {worker.role} {worker.team && `• ${worker.team}`}
                            </p>
                          </div>
                          {selectedWorkers.includes(worker.id) && (
                            <CheckCircle className="h-4 w-4 text-primary" />
                          )}
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </ScrollArea>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Batal
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={isSubmitting}
            className={cn(
              areaSafetyStatus === "still_unsafe" && "bg-destructive hover:bg-destructive/90"
            )}
          >
            {isSubmitting ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <CheckCircle className="h-4 w-4 mr-2" />
            )}
            Simpan Insiden
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}